import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

interface SiteReport {
  url: string;
  performance: number;
  accessibility: number;
  seo: number;
  bestPractices: number;
  lcp: string;
  tbt: string;
  fcp: string;
  cls: string;
  speedIndex: string;
  screenshot?: string;
}

interface ComparisonReport {
  liveSite: SiteReport;
  modernizedSite: SiteReport;
  generatedAt: string;
  htmlReports?: {
    liveSiteHtml?: string;
    modernizedSiteHtml?: string;
  };
}

interface MetricCardProps {
  title: string;
  description: string;
  businessImpact: string;
  beforeScore: number;
  afterScore: number;
  metric: 'performance' | 'accessibility' | 'seo' | 'bestPractices';
}

const getScoreColor = (score: number): string => {
  if (score >= 90) return 'bg-green-500'; // Best
  if (score >= 80) return 'bg-lime-500'; // Good
  if (score >= 70) return 'bg-yellow-500'; // OK
  return 'bg-red-500'; // Poor
};

const getScoreTextColor = (score: number): string => {
  if (score >= 90) return 'text-green-700'; // Best
  if (score >= 80) return 'text-lime-700'; // Good
  if (score >= 70) return 'text-yellow-700'; // OK
  return 'text-red-700'; // Poor
};

const getScoreBadge = (score: number): { label: string; colorClass: string } => {
  if (score >= 90) return { label: 'BEST', colorClass: 'bg-green-100 text-green-800 border-green-200' };
  if (score >= 80) return { label: 'GOOD', colorClass: 'bg-lime-100 text-lime-800 border-lime-200' };
  if (score >= 70) return { label: 'OK', colorClass: 'bg-yellow-100 text-yellow-800 border-yellow-200' };
  return { label: 'POOR', colorClass: 'bg-red-100 text-red-800 border-red-200' };
};

const calculateImprovement = (oldValue: number, newValue: number): number => {
  if (oldValue === 0) return newValue > 0 ? 100 : 0;
  return Math.round(((newValue - oldValue) / oldValue) * 100);
};

function MetricCard({ title, description, businessImpact, beforeScore, afterScore }: MetricCardProps) {
  const improvement = calculateImprovement(beforeScore, afterScore);
  const improvementText = improvement > 0 ? `+${improvement}%` : `${improvement}%`;
  const improvementColor = improvement > 0 ? 'text-green-700' : improvement < 0 ? 'text-red-700' : 'text-gray-600';
  const improvementBgColor = improvement > 0 ? 'bg-green-50 border-green-200' : improvement < 0 ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200';
  
  const beforeBadge = getScoreBadge(beforeScore);
  const afterBadge = getScoreBadge(afterScore);
  
  const pointsGained = afterScore - beforeScore;

  return (
    <div className="bg-white rounded-lg shadow-md p-6 relative overflow-hidden">
      {/* Emphasis ribbon for significant improvements */}
      {improvement > 20 && (
        <div className="absolute top-0 right-0 bg-gradient-to-l from-green-500 to-green-400 text-white px-3 py-1 text-xs font-bold rounded-bl-lg">
          🚀 MAJOR GAIN
        </div>
      )}
      
      <h3 className="text-xl font-semibold text-gray-800 mb-2">{title}</h3>
      <p className="text-gray-600 text-sm mb-1">{description}</p>
      <p className="text-blue-600 text-sm font-medium mb-4">{businessImpact}</p>
      
      {/* Large improvement callout */}
      {improvement > 0 && (
        <div className={`mb-4 p-3 rounded-lg border-2 ${improvementBgColor}`}>
          <div className="flex items-center justify-between">
            <div>
              <div className={`text-2xl font-bold ${improvementColor}`}>{improvementText}</div>
              <div className="text-sm text-gray-600">improvement</div>
            </div>
            <div className="text-right">
              <div className={`text-lg font-bold ${improvementColor}`}>+{pointsGained} pts</div>
              <div className="text-xs text-gray-600">score gain</div>
            </div>
          </div>
        </div>
      )}
      
      <div className="flex items-center justify-between mb-4">
        <div className="flex-1">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-gray-600">Before</span>
            <div className="flex items-center gap-2">
              <span className={`text-lg font-bold ${getScoreTextColor(beforeScore)}`}>
                {beforeScore}
              </span>
              <span className={`px-2 py-1 text-xs font-bold rounded border ${beforeBadge.colorClass}`}>
                {beforeBadge.label}
              </span>
            </div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div 
              className={`h-3 rounded-full ${getScoreColor(beforeScore)} transition-all duration-500`}
              style={{ width: `${beforeScore}%` }}
            />
          </div>
        </div>
        
        <div className="mx-6 flex flex-col items-center">
          <div className="text-3xl mb-1">→</div>
          {improvement > 0 && (
            <div className="text-green-600 text-xl font-bold">↗</div>
          )}
        </div>
        
        <div className="flex-1">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-gray-600">After</span>
            <div className="flex items-center gap-2">
              <span className={`text-lg font-bold ${getScoreTextColor(afterScore)}`}>
                {afterScore}
              </span>
              <span className={`px-2 py-1 text-xs font-bold rounded border ${afterBadge.colorClass}`}>
                {afterBadge.label}
              </span>
            </div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div 
              className={`h-3 rounded-full ${getScoreColor(afterScore)} transition-all duration-500`}
              style={{ width: `${afterScore}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function LoadTimeTable({ liveSite, modernizedSite }: { liveSite: SiteReport; modernizedSite: SiteReport }) {
  const metrics = [
    { label: 'First Contentful Paint', key: 'fcp', description: 'Time when first text or image appears', unit: 's' },
    { label: 'Largest Contentful Paint', key: 'lcp', description: 'Time when largest content element loads', unit: 's' },
    { label: 'Total Blocking Time', key: 'tbt', description: 'Time when main thread is blocked', unit: 'ms' },
    { label: 'Cumulative Layout Shift', key: 'cls', description: 'Visual stability score', unit: '' },
    { label: 'Speed Index', key: 'speedIndex', description: 'How quickly content is visually displayed', unit: 's' }
  ] as const;

  const parseNumericValue = (value: string): number => {
    if (!value || value === 'N/A') return 0;
    const numStr = value.replace(/[^\d.]/g, '');
    return parseFloat(numStr) || 0;
  };

  const calculateLoadTimeImprovement = (beforeStr: string, afterStr: string, unit: string): { 
    percentage: number; 
    isImprovement: boolean; 
    absoluteChange: number;
  } => {
    const before = parseNumericValue(beforeStr);
    const after = parseNumericValue(afterStr);
    
    if (before === 0 || after === 0) return { percentage: 0, isImprovement: false, absoluteChange: 0 };
    
    // For most metrics, lower is better (except CLS which is already a score)
    const isLowerBetter = unit !== '';
    const change = before - after;
    const percentage = Math.round((change / before) * 100);
    const isImprovement = isLowerBetter ? change > 0 : change < 0;
    
    return { 
      percentage: Math.abs(percentage), 
      isImprovement, 
      absoluteChange: change 
    };
  };

  const getLoadTimeBadge = (_beforeValue: string, afterValue: string): { colorClass: string; label: string } => {
    const afterNum = parseNumericValue(afterValue);
    
    // Performance thresholds (in seconds for load times)
    if (afterNum <= 1.5) return { colorClass: 'bg-green-100 text-green-800', label: 'EXCELLENT' };
    if (afterNum <= 2.5) return { colorClass: 'bg-lime-100 text-lime-800', label: 'GOOD' };
    if (afterNum <= 4.0) return { colorClass: 'bg-yellow-100 text-yellow-800', label: 'OK' };
    return { colorClass: 'bg-red-100 text-red-800', label: 'POOR' };
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h3 className="text-xl font-semibold text-gray-800 mb-4">Load Time Comparison</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b-2 border-gray-200 bg-gray-50">
              <th className="text-left py-3 px-3 font-semibold">Metric</th>
              <th className="text-left py-3 px-3 font-semibold">Before</th>
              <th className="text-left py-3 px-3 font-semibold">After</th>
              <th className="text-left py-3 px-3 font-semibold">Change</th>
              <th className="text-left py-3 px-3 font-semibold">Rating</th>
            </tr>
          </thead>
          <tbody>
            {metrics.map((metric) => {
              const beforeValue = liveSite[metric.key] || 'N/A';
              const afterValue = modernizedSite[metric.key] || 'N/A';
              const improvement = calculateLoadTimeImprovement(beforeValue, afterValue, metric.unit);
              const badge = getLoadTimeBadge(beforeValue, afterValue);
              
              return (
                <tr key={metric.key} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-4 px-3">
                    <div>
                      <div className="font-medium text-gray-900">{metric.label}</div>
                      <div className="text-xs text-gray-500 mt-1">{metric.description}</div>
                    </div>
                  </td>
                  <td className="py-4 px-3">
                    <div className="font-mono text-red-700 font-semibold">
                      {beforeValue}
                    </div>
                  </td>
                  <td className="py-4 px-3">
                    <div className="font-mono text-green-700 font-semibold">
                      {afterValue}
                    </div>
                  </td>
                  <td className="py-4 px-3">
                    {improvement.percentage > 0 && (
                      <div className={`flex items-center gap-1 ${improvement.isImprovement ? 'text-green-700' : 'text-red-700'}`}>
                        <span className="text-lg">
                          {improvement.isImprovement ? '↓' : '↑'}
                        </span>
                        <div>
                          <div className="font-bold">
                            {improvement.percentage}%
                          </div>
                          <div className="text-xs">
                            {improvement.isImprovement ? 'faster' : 'slower'}
                          </div>
                        </div>
                      </div>
                    )}
                  </td>
                  <td className="py-4 px-3">
                    <span className={`px-2 py-1 text-xs font-bold rounded ${badge.colorClass}`}>
                      {badge.label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      
      {/* Summary of improvements */}
      <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
        <h4 className="font-semibold text-green-800 mb-2">🎯 Load Time Improvements Summary</h4>
        <div className="text-sm text-green-700 space-y-1">
          {metrics.map(metric => {
            const beforeValue = liveSite[metric.key] || 'N/A';
            const afterValue = modernizedSite[metric.key] || 'N/A';
            const improvement = calculateLoadTimeImprovement(beforeValue, afterValue, metric.unit);
            
            if (improvement.isImprovement && improvement.percentage > 0) {
              return (
                <div key={metric.key}>
                  <strong>{metric.label}:</strong> {improvement.percentage}% faster ({beforeValue} → {afterValue})
                </div>
              );
            }
            return null;
          }).filter(Boolean)}
        </div>
      </div>
    </div>
  );
}

export function ModernizationReport() {
  const [report, setReport] = useState<ComparisonReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadReport = async () => {
      try {
        const response = await fetch('/reports/lighthouse-comparison.json');
        if (!response.ok) {
          throw new Error(`Report not found. Please run: npm run report <live-site-url> http://localhost:8080`);
        }
        const data = await response.json();
        setReport(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load report');
      } finally {
        setLoading(false);
      }
    };

    loadReport();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading modernization report...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-md p-8 max-w-md text-center">
          <div className="text-red-500 text-4xl mb-4">📊</div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Report Not Available</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <div className="bg-gray-100 rounded-lg p-4 text-left text-sm">
            <p className="font-medium mb-2">To generate a report:</p>
            <code className="text-blue-600">npm run report &lt;live-site-url&gt; http://localhost:8080</code>
          </div>
        </div>
      </div>
    );
  }

  if (!report) {
    return null;
  }

  const { liveSite, modernizedSite } = report;
  const reportDate = new Date(report.generatedAt).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  const metrics = [
    {
      title: "How fast your site loads",
      description: "Site speed and loading performance",
      businessImpact: "Faster load = fewer customers leave your site",
      beforeScore: liveSite.performance,
      afterScore: modernizedSite.performance,
      metric: 'performance' as const
    },
    {
      title: "How quickly people can use it",
      description: "Accessibility and usability for all users",
      businessImpact: "Better accessibility = reaches more customers",
      beforeScore: liveSite.accessibility,
      afterScore: modernizedSite.accessibility,
      metric: 'accessibility' as const
    },
    {
      title: "How Google-friendly it is",
      description: "Search engine optimization score",
      businessImpact: "Better SEO score = higher Google ranking",
      beforeScore: liveSite.seo,
      afterScore: modernizedSite.seo,
      metric: 'seo' as const
    },
    {
      title: "How well it follows web standards",
      description: "Modern web development best practices",
      businessImpact: "Better practices = more reliable and secure site",
      beforeScore: liveSite.bestPractices,
      afterScore: modernizedSite.bestPractices,
      metric: 'bestPractices' as const
    }
  ];

  return (
    <>
      <Helmet>
        <title>Site Modernization Report - Performance Comparison</title>
        <meta name="description" content="Comprehensive performance comparison between original and modernized website versions" />
      </Helmet>
      
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Your Site: Before vs. After Modernization
            </h1>
            <p className="text-xl text-gray-600 mb-2">
              Performance analysis comparing your original site with the modernized version
            </p>
            <p className="text-sm text-gray-500">
              Report generated on {reportDate}
            </p>
            <div className="mt-4 flex flex-col sm:flex-row justify-center items-center gap-2 text-sm text-gray-600">
              <span>📍 Original: <a href={liveSite.url} className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">{liveSite.url}</a></span>
              <span className="hidden sm:inline">•</span>
              <span>🚀 Modernized: <a href={modernizedSite.url} className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">{modernizedSite.url}</a></span>
            </div>
          </div>

          {/* Metrics Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {metrics.map((metric, index) => (
              <MetricCard key={index} {...metric} />
            ))}
          </div>

          {/* Load Time Table */}
          <LoadTimeTable liveSite={liveSite} modernizedSite={modernizedSite} />

          {/* Key Benefits */}
          <div className="mt-8 bg-blue-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-blue-900 mb-4">📈 Key Business Benefits</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl mb-2">⚡</div>
                <h4 className="font-medium text-blue-800">Faster Loading</h4>
                <p className="text-sm text-blue-700">Reduces bounce rate and improves user experience</p>
              </div>
              <div className="text-center">
                <div className="text-2xl mb-2">🔍</div>
                <h4 className="font-medium text-blue-800">Better SEO</h4>
                <p className="text-sm text-blue-700">Higher search engine rankings and visibility</p>
              </div>
              <div className="text-center">
                <div className="text-2xl mb-2">📱</div>
                <h4 className="font-medium text-blue-800">Mobile-First</h4>
                <p className="text-sm text-blue-700">Optimized for your biggest audience segment</p>
              </div>
            </div>
          </div>

          {/* References Section */}
          {report.htmlReports && (report.htmlReports.liveSiteHtml || report.htmlReports.modernizedSiteHtml) && (
            <div className="mt-8 bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">📋 References</h3>
              <p className="text-gray-600 text-sm mb-4">
                Detailed Lighthouse reports with comprehensive analysis, recommendations, and technical metrics.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {report.htmlReports.liveSiteHtml && (
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-800 mb-2">📍 Original Site Report</h4>
                    <p className="text-sm text-gray-600 mb-3">
                      Complete Lighthouse analysis of your current website
                    </p>
                    <a
                      href={report.htmlReports.liveSiteHtml}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                    >
                      📊 View Detailed Report
                      <span className="text-xs">↗</span>
                    </a>
                  </div>
                )}
                {report.htmlReports.modernizedSiteHtml && (
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-800 mb-2">🚀 Modernized Site Report</h4>
                    <p className="text-sm text-gray-600 mb-3">
                      Complete Lighthouse analysis of your modernized website
                    </p>
                    <a
                      href={report.htmlReports.modernizedSiteHtml}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
                    >
                      📊 View Detailed Report
                      <span className="text-xs">↗</span>
                    </a>
                  </div>
                )}
              </div>
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  💡 <strong>Tip:</strong> The detailed reports include specific recommendations, 
                  performance metrics, accessibility checks, and technical insights for developers.
                </p>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="mt-8 text-center text-sm text-gray-500">
            <p>Report generated using Google Lighthouse with mobile preset and simulated throttling</p>
            <p className="mt-1">Scores range from 0-100, with 90+ being excellent, 70-89 needing improvement, and below 70 requiring attention</p>
          </div>
        </div>
      </div>
    </>
  );
}